/**
 * How to fill an import file — shown at the top of both import cards.
 *
 * An admin meeting "Import a CSV" for the first time has a spreadsheet in one
 * hand and no idea which columns we want, in which order, or what a class code
 * is. So the card opens with the steps, a sample file to start from, and what
 * goes in each column. The sample is built in the browser from the school's
 * own class codes (and, for staff, its own subject names), so it is already a
 * valid import before the admin changes a cell; the column notes come from the
 * same guide record the parser's column list keys.
 */
import { downloadCsv, type ImportColumnGuide } from '@/lib/rosterImport';
import { Button } from '@/components/ds';

export function ImportFormatGuide({
  columns,
  sampleFileName,
  sample,
  rowNoun,
}: {
  columns: Record<string, ImportColumnGuide>;
  sampleFileName: string;
  /** Built on click, so it reads the school's classes as they are now. */
  sample: () => string[][];
  /** "student" or "teacher and class" — what one row of the file is. */
  rowNoun: string;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line px-3.5 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">How to fill the file</p>
          <ol className="mt-1 flex list-decimal flex-col gap-0.5 pl-5 text-xs text-ink-soft">
            <li>Download the sample file and open it in Excel or Google Sheets.</li>
            <li>
              Replace the sample rows with yours — one row per {rowNoun}. Keep the header row and
              the column order.
            </li>
            <li>
              Save it as CSV (Excel: <em>Save as → CSV UTF-8</em>; Google Sheets:{' '}
              <em>File → Download → .csv</em>), then choose it below.
            </li>
          </ol>
        </div>
        <Button variant="secondary" onClick={() => downloadCsv(sampleFileName, sample())}>
          Download sample file
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
              <th className="py-1.5 pr-3 text-left font-semibold text-ink">Column</th>
              <th className="py-1.5 pr-3 text-left font-semibold text-ink">Example</th>
              <th className="py-1.5 text-left font-semibold text-ink">What to put</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(columns).map(([name, column]) => (
              <tr key={name} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td className="whitespace-nowrap py-1.5 pr-3 align-top font-semibold text-ink">
                  {name}
                  <span className="ml-1 font-normal text-ink-faint">
                    {column.required ? '· required' : '· optional'}
                  </span>
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 align-top">
                  <code className="rounded bg-paper-dim px-1 py-0.5 text-forest">
                    {column.example}
                  </code>
                </td>
                <td className="py-1.5 align-top text-ink-soft">{column.help}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
